import React from 'react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SaveIcon from '@mui/icons-material/Save'

const Graphe = ({
  nodeName, setNodeName, addNode,
  startNode, endNode, nodes, selectStartNode, selectEndNode,
  generateExample, resetAlgorithm, showEdgeForm,
  selectedNode, secondSelectedNode, edgeWeight, setEdgeWeight,
  addEdge, resetEdgeSelection, canvasRef,
  handlePointerDown, handlePointerMove, handlePointerUp,
  handleRunDantzigMax, handleRunDantzigMin, exportGraphAsImage,
  styles,
}) => {
  return (
        <div className="w-full lg:w-3/4">
            <div style={styles.controlPanel}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="mb-0">
                <div>
                  <div className="top-controls-row">
                    <input type="text" value={nodeName} onChange={(e) => setNodeName(e.target.value)}
                      className="flex-1 p-2 border rounded" placeholder="Nom du sommet" />
                    <button onClick={addNode} className="ajouter">Ajouter</button>
                  </div>

                  <div className="flex gap-4 mb-0">
                    <div>
                      <label className="block mb-1 me-1">Sommet de départ:</label>
                      <select value={startNode?.id || ''}
                        onChange={(e) => {
                          const node = nodes.find(n => n.id === parseInt(e.target.value))
                          selectStartNode(node)
                        }}
                        className="p-2 selec">
                        <option value="">?</option>
                        {nodes.map(node => (
                          <option key={`start-${node.id}`} value={node.id}>
                            {node.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <label className="block mb-0 me-2">Sommet d'arrivée:</label>
                    <select value={endNode?.id || ''}
                      onChange={(e) => {
                        const node = nodes.find(n => n.id === parseInt(e.target.value))
                        selectEndNode(node)
                      }}
                      className="p-2 selec">
                      <option value="">?</option>
                      {nodes.map(node => (
                        <option key={`end-${node.id}`} value={node.id}>
                          {node.name}
                        </option>
                      ))}
                    </select>

                  </div>
                </div>

                <div>
                  <p>- Cliquez et déplacez un sommet pour le repositionner</p>
                  <p>- Maintenez Shift et cliquez sur deux sommets pour créer une distance</p>
                  <div className="flex gap-2 items-end">
                    <button onClick={generateExample} className="exemple me-3 mt-0">Générer exemple</button>
                    <Tooltip title="Réinitialiser">
                      <IconButton onClick={resetAlgorithm}
                        sx={{ color: 'darkred', '&:hover': { color: 'darkred', }, }}>
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>

              <style jsx>{`
              .top-controls-row {
                display: flex;
                align-items: center;
                gap: 12px;
                flex-wrap: wrap;
              }
              @media (max-width: 768px) {
                div[style*="grid-template-columns"] {
                  grid-template-columns: 1fr !important;
                }
              }
              `}</style>

            </div>

            {showEdgeForm && (
              <div className="flex gap-2 items-center bg-yellow-100 p-2 rounded mb-3">
                <span className="me-2">
                  {selectedNode?.name} → {secondSelectedNode?.name}
                </span>
                <input type="number" value={edgeWeight} onChange={(e) => setEdgeWeight(e.target.value)}
                  className="w-20 p-2 border rounded me-4" style={{ width: '105px' }}
                  placeholder="distance" min="1" />
                <Tooltip title="Ajouter">
                  <CheckIcon onClick={addEdge} style={{ color: 'green', cursor: 'pointer' }} className="me-4" />
                </Tooltip>
                <Tooltip title="Annuler">
                  <ClearIcon onClick={resetEdgeSelection} style={{ color: 'red', cursor: 'pointer' }} />
                </Tooltip>

              </div>
            )}

            <div style={styles.graphContainer} className="graph-container">
              <canvas
                ref={canvasRef}
                width="1400"
                height="450"
                className="w-full h-full"
                style={{ touchAction: 'pan-y', display: 'block', maxWidth: '100%' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              ></canvas>
            </div>

            <style jsx>{`
              .graph-container {
                height: 450px;
              }
              @media (max-width: 799px) and (min-width: 452px) {
                .graph-container {
                  height: 380px !important;
                }
              }
              @media (max-width: 451px) and (min-width: 380px) {
                .graph-container {
                  height: 320px !important;
                }
              }
              @media (max-width: 379px) {
                .graph-container {
                  height: 280px !important;
                }
              }
            `}</style>

            <div className="bottom-buttons-row">
              <button onClick={handleRunDantzigMax} className="maximisation">
                <span style={{ fontWeight: 'bold' }} className="me-1">Maximisation</span> <DirectionsWalkIcon />
              </button>
              <button onClick={handleRunDantzigMin} className="minimisation">
                <span style={{ fontWeight: 'bold' }} className="me-1">Minimisation</span> <DirectionsRunIcon />
              </button>
              <Tooltip title="Enregistrer">
                <IconButton onClick={exportGraphAsImage} className="save">
                  <SaveIcon sx={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)' }} />
                </IconButton>
              </Tooltip>
            </div>

            <style jsx>{`
              .bottom-buttons-row {
                display: flex;
                justify-content: center;
                align-items: center;
                flex-wrap: wrap;
                gap: 16px;
                margin-top: 1rem;
              }

              /* Rétrécissement progressif des boutons proportionnellement à l'écran */
              .maximisation, .minimisation {
                font-size: clamp(0.7rem, 3vw, 1rem);
                padding: clamp(6px, 2vw, 10px) clamp(10px, 3vw, 16px);
                white-space: nowrap;
                transition: font-size 0.15s, padding 0.15s;
              }

              .ajouter {
                font-size: clamp(0.75rem, 3vw, 1rem);
                padding: clamp(6px, 2vw, 10px) clamp(10px, 3vw, 16px);
                white-space: nowrap;
              }

              /* Générer exemple : même logique de rétrécissement que Maximisation/Minimisation */
              .exemple {
                font-size: clamp(0.7rem, 3vw, 1rem);
                padding: clamp(6px, 2vw, 10px) clamp(10px, 3vw, 16px);
                white-space: nowrap;
                transition: font-size 0.15s, padding 0.15s;
              }

              /* SaveIcon (IconButton) : rétrécit son padding et l'icône SVG interne proportionnellement */
              .save {
                padding: clamp(4px, 1.5vw, 8px) !important;
                transition: padding 0.15s;
              }
              .save :global(svg) {
                font-size: clamp(1.1rem, 4vw, 1.5rem) !important;
                transition: font-size 0.15s;
              }

              @media (max-width: 470px) {
                .bottom-buttons-row {
                  gap: 12px;
                }
                .maximisation svg, .minimisation svg {
                  font-size: 1.1rem;
                  vertical-align: middle;
                }
              }

              @media (max-width: 380px) {
                .bottom-buttons-row {
                  gap: 8px;
                }
                .maximisation, .minimisation {
                  padding: 5px 8px;
                }
                .exemple {
                  padding: 5px 8px;
                }
              }
            `}</style>
          </div>
    )
}

export default Graphe